import { Component, createRef } from 'react';
import { concat, from, Subscription } from 'rxjs';

import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { HTMLUtils } from '../../../utils/html.utils';
import { RxUtils } from '../../../utils/rx-utils';
import Modal from '../modal/modal.component';
import styles from './media-preview-modal.component.module.scss';

declare var pdfjsLib;
declare var jQuery;

let __PDF_DOC,
    __CURRENT_PAGE,
    __TOTAL_PAGES,
    __PAGE_RENDERING_IN_PROGRESS = 0,
    __CANVAS = null,
    __CANVAS_CTX = null;

function showPDF(pdf_url) {
    jQuery('#pdf-loader').show();

    pdfjsLib.getDocument({ url: pdf_url }).then(function (pdf_doc) {
        __PDF_DOC = pdf_doc;
        __TOTAL_PAGES = __PDF_DOC.numPages;

        // Hide the pdf loader and show pdf container in HTML
        jQuery('#pdf-loader').hide();
        jQuery('#pdf-contents').show();
        jQuery('#pdf-total-pages').text(__TOTAL_PAGES);

        // Show the first page
        showPage(1);
    }).catch(function (error) {
        // If error re-show the upload button
        jQuery('#pdf-loader').hide();
        jQuery('#upload-button').show();

        alert(error.message);
    });
}

function showPage(page_no) {
    __PAGE_RENDERING_IN_PROGRESS = 1;
    __CURRENT_PAGE = page_no;

    // Disable Prev & Next buttons while page is being loaded
    jQuery('#pdf-next, #pdf-prev').attr('disabled', 'disabled');

    // While page is being rendered hide the canvas and show a loading message
    jQuery('#pdf-canvas').hide();
    jQuery('#page-loader').show();

    // Update current page in HTML
    jQuery('#pdf-current-page').text(page_no);

    // Fetch the page
    __PDF_DOC.getPage(page_no).then(function (page) {
        // As the canvas is of a fixed width we need to set the scale of the viewport accordingly
        const scale_required = __CANVAS.width / page.getViewport(1).width;

        // Get viewport of the page at required scale
        const viewport = page.getViewport(scale_required);

        // Set canvas height
        __CANVAS.height = viewport.height;

        const renderContext = {
            canvasContext: __CANVAS_CTX,
            viewport: viewport,
        };

        // Render the page contents in the canvas
        page.render(renderContext).then(function () {
            __PAGE_RENDERING_IN_PROGRESS = 0;

            // Re-enable Prev & Next buttons
            jQuery('#pdf-next, #pdf-prev').removeAttr('disabled');

            // Show the canvas and hide the page loader
            jQuery('#pdf-canvas').show();
            jQuery('#page-loader').hide();
        });
    });
}

interface MediaPreviewModalComponentProps {
    url: string;
    mediaType: string;
}

export class MediaPreviewModalComponent extends Component<MediaPreviewModalComponentProps> {
    modalRef = createRef<Modal>();

    ready = false;
    readyPromise: Promise<any>;

    canvasWidth = 300;
    sub: Subscription;

    windowResizeDispatcher = () => {
        this.resized();
    }

    constructor(props) {
        super(props);
        // TODO Preact fix this
        // this.sub = modalService.onShown.subscribe(c => {
        //     this.resized();
        // });

        this.readyPromise = Promise.all([new Promise((resolve, reject) => {
            if (window['pdfjsLib']) {
                resolve({});
            } else {
                const node = document.createElement('script');
                node.src = '/js/pdf.min.js';
                node.type = 'text/javascript';
                node.async = true;
                node.charset = 'utf-8';
                node.onload = resolve;
                node.onerror = reject;
                document.getElementsByTagName('head')[0].appendChild(node);
            }
        }),
        new Promise((resolve, reject) => {
            if (window['pdfjsWorker']) {
                resolve({});
            } else {
                const node = document.createElement('script');
                node.src = '/js/pdf.worker.min.js';
                node.type = 'text/javascript';
                node.async = true;
                node.charset = 'utf-8';
                node.onload = resolve;
                node.onerror = reject;
                document.getElementsByTagName('head')[0].appendChild(node);
            }
        }),
        ]);

    }

    componentDidMount() {
        if (this.props.mediaType === 'pdf') {
            this.ready = true;
            concat(
                from(this.readyPromise),
                from(HTMLUtils.waitForElement('#pdf-canvas', 5000)),
            ).subscribe({
                next: () => {
                    __CANVAS = document.getElementById('pdf-canvas') as HTMLCanvasElement;
                    __CANVAS_CTX = __CANVAS.getContext('2d');
                    window.dispatchEvent(new Event('resize'));
                }
            })
        }
        if (BrowserUtils.hasWindow()) {
            window.addEventListener('resize', this.windowResizeDispatcher);
        }
    }

    componentWillUnmount() {
        RxUtils.safeCloseSubscriber(this.sub);
        window.removeEventListener('resize', this.windowResizeDispatcher);
    }

    private resized() {
        if (this.props.mediaType === 'pdf' && document.getElementById('pdf-contents')) {
            this.canvasWidth = document.getElementById('pdf-contents').getBoundingClientRect().width;
            if (this.canvasWidth === 0) {
                setTimeout(() => this.resized(), 1000);
            } else {
                showPDF(this.props.url);
            }
        }
    }

    showPrevious() {
        if (__CURRENT_PAGE !== 1) {
            showPage(--__CURRENT_PAGE);
        }
    }

    showNext() {
        if (__CURRENT_PAGE !== __TOTAL_PAGES) {
            showPage(++__CURRENT_PAGE);
        }

    }

    render() {
        return <Modal ref={this.modalRef}>
            <div data-modalheader>
                <h4 className="modal-title">Preview Media Live</h4>
            </div>
            <div className={`modal-body ${styles['media-preview-modal']}`} data-modalbody>
                {
                    this.props.mediaType === 'video' && <video id="video" className={`video ${styles['video-js']}`}
                        controls autoPlay={false} preload="auto" width="100%"
                        height="264" data-setup="{}">
                        <source src={this.props.url} />
                        <p className="vjs-no-js">
                            To view this video please enable JavaScript, and consider upgrading to a web browser that
                            <a href="https://videojs.com/html5-video-support/" target="_blank">supports HTML5 video</a>
                        </p>
                    </video >
                }
                {
                    this.props.mediaType === 'image' && <img src={this.props.url} className="image" />
                }
                {
                    !this.ready && <div id="pdf-loader" className={this.props.mediaType !== 'pdf' ? 'hidden' : ''}>Loading document ...</div>
                }
                {
                    this.ready && <div id="pdf-contents" className={this.props.mediaType !== 'pdf' ? 'hidden' : ''}>
                        <div id="pdf-meta">
                            <div id="pdf-buttons">
                                <button id="pdf-prev" onClick={() => this.showPrevious()}>Previous</button>
                                <button id="pdf-next" onClick={() => this.showNext()}>Next</button>
                            </div>
                            <div id="page-count-container">Page <div id="pdf-current-page"></div> of <div id="pdf-total-pages"></div>
                            </div>
                        </div>
                        <canvas id="pdf-canvas" width={this.canvasWidth}></canvas>
                        <div id="page-loader">Loading page ...</div>
                    </div>
                }
            </div >
        </Modal >
    }
}
